<div class="wrapper mat-elevation-z10">
    <div class="title">
        <span>
            <h1>{{ isOfficePayment ? 'Office' : 'Client' }} Payment Request</h1>
        </span>
        <div class="toggle">
            <div class="toggle-item">
                <span>Domestic</span>
                <mat-slide-toggle [checked]="isInternational" (change)="toggleInternational($event)"></mat-slide-toggle>
                <span>International</span>
            </div>
        </div>
    </div>

    <div class="form-container">
        <form [formGroup]="paymentRequestForm" #formDirective="ngForm" (ngSubmit)="submit(formDirective)">

            @if (!isInternational) {
            <mat-form-field class="halfLength">
                <mat-label>Payment method</mat-label>
                <mat-select formControlName="chapsRequired" (selectionChange)="onPaymentMethodChange($event.value)">
                    <mat-option *ngFor="let method of paymentMethodDropdown" [value]="method">{{ method }}</mat-option>
                </mat-select>
                @if (paymentRequestForm.get('chapsRequired')?.touched && paymentRequestForm.get('chapsRequired')?.invalid) {
                <mat-error>Please select a valid payment method.</mat-error>
                }
            </mat-form-field>
            }

            @if (showPropertyPurchaseCheckbox && !isInternational) {
            <mat-checkbox formControlName="isPropertyPurchase" (change)="onPropertyPurchaseChange($event)">
                Is this transaction related to a property purchase?
            </mat-checkbox>
            }

            @if (showPurposeCodeDropdown && !isInternational) {
            <mat-form-field class="halfLength">
                <mat-label>Purpose Code</mat-label>
                <mat-select formControlName="purposeCode">
                    <mat-option *ngFor="let p of purposeCodeDropdown" [value]="p.id">
                        {{ p.code }} - {{ p.name }}
                    </mat-option>
                </mat-select>
                @if (paymentRequestForm.get('purposeCode')?.touched && paymentRequestForm.get('purposeCode')?.invalid) {
                <mat-error>Please select a purpose code.</mat-error>
                }
            </mat-form-field>
            }

            <div class="search">
                <mat-form-field class="matterAuto">
                    <mat-label>Matter</mat-label>
                    @if (paymentRequestForm.get('matter')?.value) {
                    <button type="button" mat-icon-button matSuffix (click)="clearSearchFilter()">
                        <mat-icon>clear</mat-icon>
                    </button>
                    }
                    <input matInput formControlName="matter" [matAutocomplete]="auto" />
                    @if (paymentRequestForm.get('matter')?.touched && paymentRequestForm.get('matter')?.hasError('required')) {
                    <mat-error>Please select a matter.</mat-error>
                    }
                </mat-form-field>

                <mat-autocomplete #auto="matAutocomplete">
                    @if (isLoading) {
                    <mat-option>Loading...</mat-option>
                    } @else if (noData) {
                    <mat-option>No matching matters found.</mat-option>
                    } @else {
                    <mat-option *ngFor="let item of matterDropdownData"
                                [value]="displayMatter(item)"
                                (onSelectionChange)="lookupSelectionChanged($event)">
                        {{ item.number }} - {{ item.description }}
                    </mat-option>
                    }
                </mat-autocomplete>
            </div>

            @if (showLinkToOpenMatter()) {
            <div class="sticky-actionstep-bar">
                <div class="link-wrapper">
                    <a [href]="actionstepMatterUrl" target="_blank" rel="noopener noreferrer">
                        Open Matter in Actionstep ({{ matterNo }})
                    </a>
                    <button type="button" mat-icon-button class="small-copy-btn"
                            [cdkCopyToClipboard]="actionstepMatterUrl"
                            (cdkCopyToClipboardCopied)="onUrlCopied()"
                            matTooltip="Copy link to clipboard" matTooltipPosition="below">
                        <mat-icon>content_copy</mat-icon>
                    </button>
                </div>
                <div class="warning-box">You must be logged into Actionstep under the same jurisdiction.</div>
            </div>
            }

            <mat-form-field class="fullLength">
                <mat-label>Client Name</mat-label>
                <input matInput formControlName="clientName" readonly />
                @if (isLoading) {
                <mat-progress-spinner class="spinner-padding" matSuffix
                                      mode="indeterminate" diameter="20" strokeWidth="3" color="accent">
                </mat-progress-spinner>
                }
            </mat-form-field>

            <div class="form">

                <mat-form-field class="fullLength">
                    <mat-label>Are monies being returned directly to the client?</mat-label>
                    <mat-select formControlName="isMoniesReturned">
                        <mat-option [value]="true">Yes</mat-option>
                        <mat-option [value]="false">No</mat-option>
                    </mat-select>
                    @if (paymentRequestForm.get('isMoniesReturned')?.touched && paymentRequestForm.get('isMoniesReturned')?.invalid) {
                    <mat-error>Please select Yes or No.</mat-error>
                    }
                </mat-form-field>

                @if (paymentRequestForm.get('isMoniesReturned')?.value === false) {
                <mat-form-field class="fullLength">
                    <mat-label>If no, please explain.</mat-label>
                    <textarea matInput formControlName="returnMoniesExplanationIfNo"></textarea>
                    @if (paymentRequestForm.get('returnMoniesExplanationIfNo')?.touched &&
                    paymentRequestForm.get('returnMoniesExplanationIfNo')?.hasError('required')) {
                    <mat-error>Please provide an explanation if monies are not being returned directly to the client.</mat-error>
                    }
                </mat-form-field>
                }

                <mat-form-field class="fullLength">
                    <mat-label>Reason for payment</mat-label>
                    <textarea matInput formControlName="reason"></textarea>
                    @if (paymentRequestForm.get('reason')?.touched && paymentRequestForm.get('reason')?.hasError('required')) {
                    <mat-error>Reason for payment is required.</mat-error>
                    }
                </mat-form-field>

                @if (isUserPartner) {
                <mat-form-field class="fullLength">
                    <mat-label>Are you the matter partner on this matter?</mat-label>
                    <mat-select formControlName="matterPartner">
                        <mat-option [value]="true">Yes</mat-option>
                        <mat-option [value]="false">No</mat-option>
                    </mat-select>
                </mat-form-field>
                }

                @if (isUserPartner && !paymentRequestForm.get('matterPartner')?.value) {
                <mat-form-field class="fullLength">
                    <mat-label>Reason for requesting payment on a matter you are not the matter partner for</mat-label>
                    <textarea matInput formControlName="nonPartnerReason"></textarea>
                    @if (paymentRequestForm.get('nonPartnerReason')?.touched &&
                    paymentRequestForm.get('nonPartnerReason')?.hasError('required')) {
                    <mat-error>Reason is required when you are not the matter partner.</mat-error>
                    }
                </mat-form-field>
                }

                @if (isInternational || showPropertyPurchaseCheckbox) {
                <mat-form-field class="fullLength">
                    <mat-label>Address</mat-label>
                    <textarea matInput formControlName="address" [required]="isInternational"></textarea>
                    @if (paymentRequestForm.get('address')?.touched && paymentRequestForm.get('address')?.hasError('required')) {
                    <mat-error>Address for payment is required.</mat-error>
                    }
                </mat-form-field>
                }

                <div class="header">Payment</div>

                <div class="date-time-row">
                    <mat-form-field color="accent" class="date-field">
                        <mat-label>Requested Date</mat-label>
                        <input matInput [matDatepicker]="requestedDatePicker" formControlName="dateRequested" placeholder="dd/MM/yyyy" [max]="currentDate"/>
                        <mat-datepicker-toggle matSuffix [for]="requestedDatePicker" />
                        <mat-datepicker #requestedDatePicker />
                        @if (paymentRequestForm.get('dateRequested')?.touched && paymentRequestForm.get('dateRequested')?.invalid) {
                        <mat-error>Please select a date.</mat-error>
                        }
                    </mat-form-field>

                    <mat-form-field color="accent" class="time-field">
                        <mat-label>Requested Time</mat-label>
                        <input matInput type="time" formControlName="requestedTime" placeholder="HH:mm" />
                        @if (paymentRequestForm.get('requestedTime')?.touched && paymentRequestForm.get('requestedTime')?.invalid) {
                        <mat-error>Please enter a time.</mat-error>
                        }
                    </mat-form-field>
                </div>

                @if (showPayeeSelect) {
                <mat-form-field>
                    <mat-label>Payee</mat-label>
                    <mat-select multiple required panelClass="sw-multi-select-panel" [formControl]="payeeClients"
                                [errorStateMatcher]="payeeErrorMatcher"
                                (selectionChange)="onPayeeClientsChange()"
                                (openedChange)="onPayeeSelectOpened($event)">
                        @for (name of clientOptions; track name) {
                        <mat-option [value]="name">{{ name }}</mat-option>
                        }
                    </mat-select>
                    <mat-error>Please select at least one payee.</mat-error>
                </mat-form-field>
                } @else {
                <mat-form-field>
                    <mat-label>Payee</mat-label>
                    <input matInput formControlName="payee" [readonly]="isPayeeReadOnly" />
                    @if (isLoading) {
                    <mat-progress-spinner class="spinner-padding" matSuffix
                                          mode="indeterminate" diameter="20" strokeWidth="3" color="accent">
                    </mat-progress-spinner>
                    }
                    @if (paymentRequestForm.get('payee')?.touched && paymentRequestForm.get('payee')?.invalid) {
                    <mat-error>Please enter a valid payee.</mat-error>
                    }
                </mat-form-field>
                }

                <div [ngClass]="{'form': isOfficePayment}">
                    <mat-form-field [ngClass]="{'w-100': !isOfficePayment}">
                        <mat-label>Amount</mat-label>
                        @if (!isInternational) {
                        <span matTextPrefix>£&nbsp;</span>
                        }
                        <input matInput formControlName="amount" type="text"
                               (keydown)="validateInput($event)" maxlength="15" (blur)="formatNumber()" />
                        @if (paymentRequestForm.get('amount')?.touched && paymentRequestForm.get('amount')?.invalid) {
                        <mat-error>Please enter a valid amount.</mat-error>
                        }
                    </mat-form-field>

                    @if (isOfficePayment) {
                    <mat-form-field>
                        <mat-label>VAT Amount</mat-label>
                        <input matInput formControlName="vatAmount" type="text"
                               (keydown)="validateInput($event)" maxlength="15" (blur)="formatVatNumber()" />
                        @if (paymentRequestForm.get('vatAmount')?.touched && paymentRequestForm.get('vatAmount')?.invalid) {
                        <mat-error>Please enter a valid VAT amount.</mat-error>
                        }
                    </mat-form-field>
                    }
                </div>

                <mat-form-field>
                    <mat-label>{{ isOfficePayment ? 'Office bank' : 'Client bank' }}</mat-label>
                    <mat-select formControlName="clientBankAccount">
                        <mat-option *ngFor="let account of filteredBankAccounts" [value]="account">
                            {{ account.accountId }} - {{ account.description }}
                        </mat-option>
                    </mat-select>
                    @if (paymentRequestForm.get('clientBankAccount')?.touched &&
                    paymentRequestForm.get('clientBankAccount')?.hasError('required')) {
                    <mat-error>Please select a bank account.</mat-error>
                    }
                </mat-form-field>

                @if (showLinkToOpenMatter()) {
                <div class="actionstep-matter-overview">
                    <div class="link-wrapper">
                        <a [href]="actionstepMatterOverviewUrl" target="_blank" rel="noopener noreferrer">
                            Open Actionstep Matter ({{ matterNo }}) Overview to view linked accounts
                        </a>
                        <button type="button" mat-icon-button class="small-copy-btn"
                                [cdkCopyToClipboard]="actionstepMatterOverviewUrl"
                                (cdkCopyToClipboardCopied)="onUrlCopied()"
                                matTooltip="Copy link to clipboard" matTooltipPosition="below">
                            <mat-icon>content_copy</mat-icon>
                        </button>
                    </div>
                    <div class="warning-box">You must be logged into Actionstep under the same jurisdiction.</div>
                </div>
                }

                @if (isInternational) {
                <mat-form-field>
                    <mat-label>Currency</mat-label>
                    <mat-select formControlName="currency">
                        <mat-option *ngFor="let c of currencyDropdown" [value]="c.currencyCode">
                            {{ c.currencyCode }} - {{ c.symbol }}
                        </mat-option>
                    </mat-select>
                    <mat-error>Please select a currency code.</mat-error>
                </mat-form-field>

                <div class="header">Intermediate Bank (if any)</div>
                }

                @if (!isInternational) {
                <mat-form-field>
                    <mat-label>Bank</mat-label>
                    <input matInput formControlName="bankName" />
                    @if (paymentRequestForm.get('bankName')?.touched &&
                    paymentRequestForm.get('bankName')?.hasError('required')) {
                    <mat-error>Please enter the name of the bank.</mat-error>
                    }
                </mat-form-field>
                }

                @if (isInternational) {
                <mat-form-field>
                    <mat-label>Intermediary Bank</mat-label>
                    <input matInput formControlName="intermediaryBankName" />
                    @if (paymentRequestForm.get('intermediaryBankName')?.touched &&
                    paymentRequestForm.get('intermediaryBankName')?.hasError('required')) {
                    <mat-error>Please enter the name of the intermediary bank.</mat-error>
                    }
                </mat-form-field>
                }

                @if (!isInternational) {
                <div class="form">
                    <mat-form-field>
                        <mat-label>Sort code</mat-label>
                        <input matInput maxlength="6" inputmode="numeric" formControlName="sortCode"
                               (keypress)="allowOnlyDigits($event)" (input)="sanitizeSortCode($event)" />
                        @if (paymentRequestForm.get('sortCode')?.touched && paymentRequestForm.get('sortCode')?.invalid) {
                        <mat-error>Please enter a valid 6-digit sort code (e.g. 123456).</mat-error>
                        }
                    </mat-form-field>

                    <mat-form-field>
                        <mat-label>Account number</mat-label>
                        <input type="text" maxlength="34" matInput formControlName="accountNumber" />
                        @if (paymentRequestForm.get('accountNumber')?.touched &&
                        paymentRequestForm.get('accountNumber')?.invalid) {
                        <mat-error>Please enter a valid account number.</mat-error>
                        }
                    </mat-form-field>
                </div>
                }

                @if (!isInternational) {
                <mat-form-field>
                    <mat-label>Payee reference</mat-label>
                    <input matInput formControlName="payeeReference" />
                    @if (paymentRequestForm.get('payeeReference')?.touched &&
                    paymentRequestForm.get('payeeReference')?.invalid) {
                    <mat-error>Please enter a valid payee reference.</mat-error>
                    }
                </mat-form-field>
                }

                @if (isInternational) {
                <mat-form-field>
                    <mat-label>Intermediary account number / IBAN</mat-label>
                    <input maxlength="34" type="text" matInput formControlName="intermediaryAccountNumber" />
                    @if (paymentRequestForm.get('intermediaryAccountNumber')?.touched &&
                    paymentRequestForm.get('intermediaryAccountNumber')?.invalid) {
                    <mat-error>Please enter a valid intermediary account number / IBAN.</mat-error>
                    }
                </mat-form-field>

                <mat-form-field>
                    <mat-label>Intermediary SWIFT / BIC code</mat-label>
                    <input matInput type="text" maxlength="11" formControlName="intermediarySwiftBicCode" />
                    @if (paymentRequestForm.get('intermediarySwiftBicCode')?.touched &&
                    paymentRequestForm.get('intermediarySwiftBicCode')?.invalid) {
                    <mat-error>Please enter a valid intermediary SWIFT / BIC code.</mat-error>
                    }
                </mat-form-field>

                <mat-form-field>
                    <mat-label>Intermediary Reference</mat-label>
                    <input matInput formControlName="intermediaryReference" />
                    @if (paymentRequestForm.get('intermediaryReference')?.touched &&
                    paymentRequestForm.get('intermediaryReference')?.invalid) {
                    <mat-error>Please enter a valid intermediary reference.</mat-error>
                    }
                </mat-form-field>

                <div class="header">Final Bank</div>

                <mat-form-field>
                    <mat-label>Bank</mat-label>
                    <input matInput formControlName="bankName" />
                    @if (paymentRequestForm.get('bankName')?.touched &&
                    paymentRequestForm.get('bankName')?.invalid) {
                    <mat-error>Please enter the name of the bank.</mat-error>
                    }
                </mat-form-field>

                <mat-form-field>
                    <mat-label>Final beneficiary account number / IBAN</mat-label>
                    <input maxlength="34" type="text" matInput formControlName="beneficiaryAccountNumber" />
                    @if (paymentRequestForm.get('beneficiaryAccountNumber')?.touched &&
                    paymentRequestForm.get('beneficiaryAccountNumber')?.invalid) {
                    <mat-error>Please enter a valid final beneficiary account number / IBAN.</mat-error>
                    }
                </mat-form-field>

                <mat-form-field>
                    <mat-label>Final beneficiary SWIFT / BIC code</mat-label>
                    <input matInput type="text" maxlength="11" formControlName="swiftBicCode" />
                    @if (paymentRequestForm.get('swiftBicCode')?.touched &&
                    paymentRequestForm.get('swiftBicCode')?.invalid) {
                    <mat-error>Please enter a valid SWIFT / BIC code.</mat-error>
                    }
                </mat-form-field>

                <mat-form-field>
                    <mat-label>Final bank reference</mat-label>
                    <input matInput formControlName="finalReference" />
                    @if (paymentRequestForm.get('finalReference')?.touched &&
                    paymentRequestForm.get('finalReference')?.invalid) {
                    <mat-error>Please enter a valid reference.</mat-error>
                    }
                </mat-form-field>

                <mat-form-field>
                    <mat-label>Payee reference</mat-label>
                    <input matInput formControlName="payeeReference" />
                    @if (paymentRequestForm.get('payeeReference')?.touched &&
                    paymentRequestForm.get('payeeReference')?.invalid) {
                    <mat-error>Please enter a valid payee reference.</mat-error>
                    }
                </mat-form-field>
                }

                <mat-form-field class="fullLength">
                    <mat-label>Note to accounts</mat-label>
                    <textarea matInput formControlName="otherInformation"></textarea>
                </mat-form-field>

                @if (isInternational) {
                <div class="displayText">International Transfer Fee of £30 + VAT payable</div>
                }

                <mat-form-field class="fullLength">
                    <mat-label>Have the account details been verbally verified by telephone?</mat-label>
                    <mat-select formControlName="isAccountVerified">
                        <mat-option value="Yes">Yes</mat-option>
                        <mat-option value="No">No</mat-option>
                    </mat-select>
                    @if (paymentRequestForm.get('isAccountVerified')?.touched &&
                    paymentRequestForm.get('isAccountVerified')?.hasError('required')) {
                    <mat-error>Please select Yes or No.</mat-error>
                    }
                </mat-form-field>

                @if (paymentRequestForm.get('isAccountVerified')?.value === 'Yes') {
                <mat-form-field color="accent">
                    <mat-label>Date of verification</mat-label>
                    <input matInput [matDatepicker]="verificationDatePicker" [max]="currentDate"
                           formControlName="telephoneVerificationDate" />
                    <mat-datepicker-toggle matSuffix [for]="verificationDatePicker"></mat-datepicker-toggle>
                    <mat-datepicker #verificationDatePicker></mat-datepicker>
                    @if (paymentRequestForm.get('telephoneVerificationDate')?.touched &&
                    paymentRequestForm.get('telephoneVerificationDate')?.hasError('required')) {
                    <mat-error>Please select a date.</mat-error>
                    }
                </mat-form-field>

                <mat-form-field>
                    <mat-label>Who did you speak to?</mat-label>
                    <input matInput formControlName="verificationSpokeTo" />
                    @if (paymentRequestForm.get('verificationSpokeTo')?.touched &&
                    paymentRequestForm.get('verificationSpokeTo')?.hasError('required')) {
                    <mat-error>Please enter who you spoke to.</mat-error>
                    }
                </mat-form-field>
                }

                @if (paymentRequestForm.get('isAccountVerified')?.value === 'No') {
                <mat-form-field class="fullLength">
                    <mat-label>Reason for not confirming details by phone</mat-label>
                    <textarea matInput formControlName="verificationNoReason"></textarea>
                    @if (paymentRequestForm.get('verificationNoReason')?.touched &&
                    paymentRequestForm.get('verificationNoReason')?.hasError('required')) {
                    <mat-error>Please provide a reason.</mat-error>
                    }
                </mat-form-field>
                }

                @if (isUserPartner === false) {
                <div class="fullLength">
                    <mat-form-field>
                        <mat-label>Partner authorising</mat-label>
                        <mat-select formControlName="partnerAuthorisingId">
                            <mat-option *ngFor="let item of partnerList" [value]="item.id">
                                {{ item.name }}
                            </mat-option>
                        </mat-select>
                        @if (paymentRequestForm.get('partnerAuthorisingId')?.touched &&
                        paymentRequestForm.get('partnerAuthorisingId')?.hasError('required')) {
                        <mat-error>Partner authorising is required.</mat-error>
                        }
                    </mat-form-field>
                </div>
                }

                <xp-draft-documents class="fullLength"
                                    [documents]="draftEditor.documents()"
                                    (remove)="removeDraftDocument($event)" />

                <mat-form-field class="fullLength">
                    <mat-label>Files to upload<span class="custom-required-marker">*</span></mat-label>
                    <input matInput [value]="displayFile.join(', ')" placeholder="Please select a file" readonly />
                    <button type="button" (click)="fileInput.click()" mat-icon-button matSuffix>
                        <mat-icon>upload_file</mat-icon>
                    </button>
                    @if (displayFile.length > 0) {
                    <button type="button" mat-icon-button matSuffix (click)="clearUpload()">
                        <mat-icon>clear</mat-icon>
                    </button>
                    }
                    <input type="file" #fileInput (change)="onFilesSelected($any($event.target))" multiple hidden />
                    @if (paymentRequestForm.get('uploadedFiles')?.touched &&
                    paymentRequestForm.get('uploadedFiles')?.hasError('required')) {
                    <mat-error>At least one file attachment is required.</mat-error>
                    }
                </mat-form-field>

                @if (isUserPartner) {
                <mat-checkbox class="fullLength" formControlName="fundsCheckedByPartner">
                    I confirm that available funds have been checked.<span class="important">*</span>
                </mat-checkbox>

                <div class="fullLength">
                    <mat-checkbox formControlName="isPartnerCertified">
                        I certify that<span class="important">*</span>
                    </mat-checkbox>
                    <ul>
                        <li>The information contained in this form has been verified by me and is accurate.</li>
                        <li>This payment is in accordance with the Accounts Policy (including Direct Policy)* and the AML and Sanctions Policy.</li>
                        <li>I have undertaken source of funds checks and that they are satisfactory.</li>
                    </ul>
                    <p>If any doubt, please contact a senior member of the Compliance Team.</p>
                </div>
                }

            </div>
            <div class="action-button">
                <xp-draft-actions [draftName]="draftEditor.draftName()"
                                  [busy]="draftEditor.saving()"
                                  [disabled]="!canSaveDraft || submitBtn"
                                  (saveDraft)="saveDraftAndReset(formDirective)" />
                <button color="accent" type="submit" mat-raised-button class="mat-accent submit-btn"
                        [disabled]="submitBtn || draftEditor.saving() || paymentRequestForm.invalid || paymentRequestForm.untouched">
                    {{ isUserPartner ? 'Submit For Account Approval' : 'Submit For Partner Approval' }}
                </button>
            </div>

        </form>
    </div>
</div>
